import React, { useState, useEffect, useRef } from 'react';
import styles from './EditableField.module.css';
import { useTheme } from '../../../contexts/ThemeContext';
import { useTenancy } from '../../../contexts/TenancyContext';
import { cleanDateAndTimeObject, renderDate, toLocalDateString } from '../../../../shared/renderDate';
import { colorValues } from '../../../contexts/ThemeContext';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../tooltip';
import { Input } from '../input';
import { Textarea } from '../textarea';
import { Button } from '../button';
import { Badge } from '../badge';
import { DatePicker } from '../date-picker';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../select';
import { Label } from '../label';
import { CountrySearchCombobox } from '../CountrySearchCombobox';
import { getPopularCountries } from '../../../utils/country-utils'
import { DataTypesProvider, DataTypeCombobox, useDataTypes } from '../DataTypeCombobox';
import { ControlCombobox } from '../ControlCombobox';
import { LegalObligationsProvider, LegalObligationsCombobox, useLegalObligations } from '../LegalObligationsCombobox';
import { RisksCombobox, RisksProvider } from '../RisksCombobox';
import { AssetsCombobox, AssetsProvider } from '../AssetsCombobox';
import { SingleUserCombobox } from '../SingleUserCombobox';
import { MultiUserCombobox } from '../MultiUserCombobox';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPenToSquare } from '@awesome.me/kit-269fbef2e2/icons/duotone/solid';

// Renders data type names as badges given an array of IDs
const RenderDataTypeBadges: React.FC<{ ids: string[] }> = ({ ids }) => {
  const { dataTypes } = useDataTypes ? useDataTypes() : { dataTypes: [] };
  console.debug('dataTypes', dataTypes)
  const idToName = (id: string) => dataTypes.find(dt => dt.dataTypeID === id || dt.id === id)?.dataTypeName || dataTypes.find(dt => dt.dataTypeID === id || dt.id === id)?.name || id;
  return (
    <div className="flex flex-wrap gap-1">
      {ids.length > 0 ? ids.map((id, i) => (
        <div
          key={id + i}
          className="flex items-center gap-1 bg-primary/10 text-primary rounded-md px-2 py-1 text-sm"
        >
          <span>{idToName(id)}</span>
        </div>

      )) : '-'}
    </div>
  );
};

// Renders legal obligation names as badges given an array of IDs
const RenderLegalObligationBadges: React.FC<{ ids: string[] }> = ({ ids }) => {
  const { obligations } = useLegalObligations();
  const idToName = (id: string) => obligations.find(o => o.id === id)?.requirement || id;
  return (
    <div className="flex flex-wrap gap-1">
      {ids.length > 0 ? ids.map((id, i) => (
        <Badge key={id + i} variant="secondary" className="text-xs">
          {idToName(id)}
        </Badge>
      )) : '-'}
    </div>
  );
};
interface OptionType {
  key: string;
  value: string;
}

type SelectOption = string | OptionType;

interface HiddenCondition {
  key: string;
  value: string;
}

interface ShouldHideCondition {
  fields: string[];
  when: boolean;
}

interface EditableFieldProps {
  id?: string;
  label: string;
  value: string | string[];
  type?: 'text' | 'select' | 'tags' | 'date' | 'textarea' | 'markdown' | 'controlcombobox' | 'datatypecombobox' | 'legalobligations' | 'url' | 'riskscombobox' | 'assetscombobox' | 'singleuser' | 'multiuser';
  options?: SelectOption[];
  onSave?: (newValue: string | string[] | Date) => void;
  disabled?: boolean;
  editing?: boolean;
  setEditing?: (v: boolean) => void;
  readOnly?: boolean;
  fullWidth?: boolean;
  hideActionButton?: boolean;
  hidden?: HiddenCondition;
  shouldHide?: ShouldHideCondition;
  formValues?: Record<string, any>; // To check against hidden condition
  isCreateMode?: boolean;
  pencilAlways?: boolean;
  showSecurityScore?: boolean;
  securityScore?: number | null;
  isCheckingSecurity?: boolean;
  securityCheckError?: string | null;
  onSecurityCheck?: (assetID?: string | undefined, url?: string | undefined) => void;
}

const EditableField: React.FC<EditableFieldProps> = ({
  id,
  label,
  value,
  type = 'text',
  options = [],
  onSave,
  disabled = false,
  editing: controlledEditing,
  setEditing: controlledSetEditing,
  readOnly = false,
  fullWidth = false,
  hideActionButton = false,
  hidden,
  shouldHide,
  formValues = {},
  isCreateMode = false,
  pencilAlways = false,
  showSecurityScore = false,
  securityScore,
  isCheckingSecurity = false,
  securityCheckError,
  onSecurityCheck
}) => {
  const theme = useTheme();
  const { userIdToName } = useTenancy();
  const [localEditing, setLocalEditing] = useState(false);
  const [editValue, setEditValue] = useState<string | string[]>(value as string | string[]);
  const [newTag, setNewTag] = useState('');
  const editing = controlledEditing !== undefined ? controlledEditing : localEditing;
  const setEditing = controlledSetEditing !== undefined ? controlledSetEditing : setLocalEditing;
  const containerRef = useRef<HTMLDivElement>(null);

  // Ref for markdown textarea auto-resize
  const markdownTextareaRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (!editing || !containerRef.current) return;

      // Check if click is directly on the body or outside our component
      if (event.target === document.body || !containerRef.current.contains(event.target as Node)) {
        // Check if click is inside any open popovers or dialogs
        const popoverSelectors = [
          '[data-slot="select-content"]',
          '[data-slot="popover-content"]',
          '.popover-content',
          '[role="dialog"]',
          '[role="menu"]'
        ];

        const isInPopover = popoverSelectors.some(selector =>
          document.querySelector(selector)?.contains(event.target as Node)
        );

        if (!isInPopover) {
          // Only save if the input actually has focus
          const activeElement = document.activeElement;
          const isInputFocused = containerRef.current.contains(activeElement);

          if (isInputFocused) {
            handleSave();
          }
        }
      }
    };

    // Use a slight delay to avoid capturing the click that opened the editor
    const timer = setTimeout(() => {
      if (editing) {
        document.addEventListener('mousedown', handleClickOutside, true); // Use capture phase
      }
    }, 100);

    return () => {
      clearTimeout(timer);
      document.removeEventListener('mousedown', handleClickOutside, true);
    };
  }, [editing, editValue, isCreateMode, hideActionButton]);
  // Update editValue when the value prop changes (important for historical view)
  useEffect(() => {
    setEditValue(value as string | string[]);
  }, [value]);

  // Update editValue when value or editing state changes
  useEffect(() => {
    setEditValue(value as string | string[]);
  }, [value, editing]);

  // Auto-save effect for create mode (when hideActionButton is true)
  useEffect(() => {
    // Only run this effect when editing, hideActionButton is true, and there's a save handler
    if (editing && hideActionButton && onSave && editValue !== undefined) {
      // Add a small debounce to avoid too many saves
      const timer = setTimeout(() => {
        if (JSON.stringify(editValue) !== JSON.stringify(value)) {
          console.log('editValue', editValue);

          console.log(`Auto-saving field ${label}: ${editValue} (from useEffect)`);
          // Use toLocalDateString for date fields to avoid timezone shifts
          const valueToSave = type === 'date' ? (editValue ? editValue : '') : editValue;
          onSave(valueToSave);
        }
      }, 300);

      return () => clearTimeout(timer);
    }
  }, [editValue, hideActionButton, editing, onSave, type, value, label]);

  const handleSave = () => {
    setEditing(false);
    if (onSave) {
      // For date fields, we need to handle the date object conversion
      if (type === 'date') {
        const dateValue = typeof editValue === 'string' ? new Date(editValue) : editValue;
        // console.log('dateValue', dateValue);
        const valueToSave = dateValue ? dateValue : '';
        onSave(valueToSave);
      } else {
        onSave(editValue);
      }
    }
  };

  const handleCancel = () => {
    setEditing(false);
  };

  // Unified edit mode: render the right input, then the same Save/Cancel buttons
  // Debounce timer for security check (must be at the top level, outside all conditionals)
  const debounceTimer = React.useRef<NodeJS.Timeout | null>(null);
  useEffect(() => {
    return () => {
      if (debounceTimer.current) {
        clearTimeout(debounceTimer.current);
      }
    };
  }, []);

  // Auto-resize effect for markdown textarea
  useEffect(() => {
    if (editing && type === 'markdown' && markdownTextareaRef.current) {
      const textarea = markdownTextareaRef.current;
      textarea.style.height = 'auto';
      textarea.style.height = textarea.scrollHeight + 'px';
    }
  }, [editing, type, editValue]);

  if (editing) {
    let inputNode = null;
    if (type === 'select') {
      inputNode = (
        <Select value={editValue as string} onValueChange={setEditValue} disabled={disabled}>
          <SelectTrigger
            className={styles['editable-input']}
            style={{
              backgroundColor: theme.isDark ? '#232326' : '#fff',
              color: theme.isDark ? '#f3f4f6' : '#18181b',
              border: `1px solid ${theme.isDark ? '#35353a' : '#e5e7eb'}`
            }}
          >
            <SelectValue placeholder="Select..." />
          </SelectTrigger>
          <SelectContent
            style={{
              backgroundColor: theme.isDark ? '#232326' : '#fff',
              color: theme.isDark ? '#f3f4f6' : '#18181b',
              border: `1px solid ${theme.isDark ? '#35353a' : '#e5e7eb'}`
            }}
          >
            {(options as Array<string | OptionType>).map((opt, index) => {
              if (typeof opt === 'string') {
                return (
                  <SelectItem key={`${opt}-${index}`} value={opt}>
                    {opt}
                  </SelectItem>
                );
              }
              return (
                <SelectItem key={opt.key} value={opt.value}>
                  {opt.key}
                </SelectItem>
              );
            })}
          </SelectContent>
        </Select>
      );
    } else if (type === 'date') {
      // Handle various date formats (Firebase Timestamp, __time__ objects, strings, numbers, etc.)
      const dateValue = (() => {
        // Handle undefined or null
        if (!editValue) return undefined;

        // Handle different date formats
        if (typeof editValue === 'object') {
          // Handle Firebase Timestamp objects (with toDate function)
          if (editValue.toDate && typeof editValue.toDate === 'function') {
            return editValue.toDate();
          }

          // Handle objects with __time__ property
          if (editValue.__time__) {
            return new Date(editValue.__time__);
          }

          // Handle Date objects
          if (editValue instanceof Date) {
            return editValue;
          }

          // Try to extract seconds and nanoseconds from Firestore timestamp-like objects
          if (editValue.seconds !== undefined && typeof editValue.seconds === 'number') {
            const seconds = editValue.seconds;
            const nanoseconds = editValue.nanoseconds || 0;
            return new Date(seconds * 1000 + nanoseconds / 1000000);
          }

          // Try as-is as last resort for objects
          try {
            return new Date(editValue);
          } catch (e) {
            console.warn('Failed to parse object as date:', editValue);
            return undefined;
          }
        }

        // Handle string (ISO or YYYY-MM-DD)
        if (typeof editValue === 'string' && editValue) {
          // Prefer parsing as YYYY-MM-DD in local time to avoid UTC shift
          if (/^\d{4}-\d{2}-\d{2}$/.test(editValue)) {
            const [year, month, day] = editValue.split('-').map(Number);
            const localDate = new Date(year, month - 1, day);
            return localDate;
          }
          // Try parsing as ISO string
          const date = new Date(editValue);
          return isNaN(date.getTime()) ? undefined : date;
        }

        // Handle numbers (timestamp in milliseconds)
        if (typeof editValue === 'number') {
          return new Date(editValue);
        }

        return undefined;
      })();

      inputNode = (
        <div className={styles['editable-input-wrapper']}>
          <DatePicker
            date={dateValue}
            setDate={(date) => {
              // Store the date as an ISO string
              setEditValue(date ? date.toISOString() : '');
            }}
            readOnly={disabled}
            fullWidth={true}
            enableNaturalInput
            showPreview
            placeholder="Type 'tomorrow' or pick a date"
          />
        </div>
      );
    } else if (type === 'controlcombobox') {
      // Ensure we have an array for controls
      const controlsArray = Array.isArray(editValue)
        ? editValue
        : (typeof editValue === 'string' && editValue)
          ? editValue.split(",").map(control => control.trim())
          : [];

      inputNode = (
        <div className={fullWidth ? "w-full" : "max-w-md"}>
          <ControlCombobox
            selected={controlsArray}
            setSelected={(controls) => setEditValue(controls)}
            placeholder="Search for controls..."
            className="w-full"
            fullWidth={fullWidth}
            onControlSelect={(controlValue) => {
              if (!controlsArray.includes(controlValue)) {
                setEditValue([...controlsArray, controlValue]);
              }
            }}
            onControlDeSelect={(controlValue) => {
              setEditValue(controlsArray.filter(control => control !== controlValue));
            }}
          />
        </div>
      );
    } else if (type === 'datatypecombobox') {
      // DataTypeCombobox variant
      const dataTypeArray = Array.isArray(editValue)
        ? editValue
        : (typeof editValue === 'string' && editValue)
          ? editValue.split(",").map(dt => dt.trim())
          : [];

      inputNode = (
        <DataTypesProvider>
          <div className={fullWidth ? "w-full" : "max-w-md"}>
            <DataTypeCombobox
              selected={dataTypeArray}
              setSelected={setEditValue}
              placeholder={`Select ${label || 'data types'}...`}
              className="w-full"
              fullWidth={fullWidth}
            />
          </div>
        </DataTypesProvider>
      );
    } else if (type === 'riskscombobox') {
      const risksArray = Array.isArray(editValue)
        ? editValue
        : (typeof editValue === 'string' && editValue)
          ? editValue.split(',').map(risk => risk.trim()).filter(Boolean)
          : [];

      inputNode = (
        <RisksProvider>
          <div className={fullWidth ? 'w-full' : 'max-w-md'}>
            <RisksCombobox
              selected={risksArray}
              setSelected={(values) => setEditValue(values)}
              placeholder={`Select ${label || 'risks'}...`}
              className="w-full"
              fullWidth={fullWidth}
            />
          </div>
        </RisksProvider>
      );
    } else if (type === 'assetscombobox') {
      const assetsArray = Array.isArray(editValue)
        ? editValue.map(value => value?.toString?.() ?? '')
        : (typeof editValue === 'string' && editValue)
          ? editValue.split(',').map(asset => asset.trim()).filter(Boolean)
          : [];

      inputNode = (
        <AssetsProvider>
          <div className={fullWidth ? 'w-full' : 'max-w-md'}>
            <AssetsCombobox
              selected={assetsArray}
              setSelected={(values) => {
                if (typeof values === 'function') {
                  setEditValue(prev => {
                    const prevArray = Array.isArray(prev)
                      ? prev.map(value => value?.toString?.() ?? '')
                      : (typeof prev === 'string' && prev)
                        ? prev.split(',').map(item => item.trim()).filter(Boolean)
                        : [];
                    const next = values(prevArray);
                    return Array.isArray(next)
                      ? next.map(value => value?.toString?.() ?? '')
                      : [];
                  });
                } else {
                  const next = Array.isArray(values)
                    ? values.map(value => value?.toString?.() ?? '')
                    : [];
                  setEditValue(next);
                }
              }}
              placeholder={`Select ${label || 'assets'}...`}
              className="w-full"
              fullWidth={fullWidth}
            />
          </div>
        </AssetsProvider>
      );
    } else if (type === 'legalobligations') {
      // LegalObligationsCombobox variant
      const obligationsArray = Array.isArray(editValue)
        ? editValue
        : (typeof editValue === 'string' && editValue)
          ? editValue.split(",").map(id => id.trim())
          : [];

      inputNode = (
        <LegalObligationsProvider>
          <div className={fullWidth ? "w-full" : "max-w-md"}>
            <LegalObligationsCombobox
              selected={obligationsArray}
              setSelected={setEditValue}
              placeholder={`Select ${label || 'legal obligations'}...`}
              className="w-full"
              fullWidth={fullWidth}
            />
          </div>
        </LegalObligationsProvider>
      );
    } else if (type === 'tags') {
      // If options are provided, render a multi-select dropdown (combobox style)
      if (options && Array.isArray(options) && options.length > 0) {
        // Defensive: always treat editValue as array
        const tagArray = Array.isArray(editValue)
          ? editValue
          : (typeof editValue === 'string' && editValue)
            ? editValue.split(",").map(tag => tag.trim())
            : [];

        // Normalize options to { key, value } objects
        const normalizedOptions = (options as Array<string | OptionType>).map(opt =>
          typeof opt === 'string' ? { key: opt, value: opt } : opt
        );

        // Handler for removing tag via badge X
        const handleRemoveTag = (tagValue: string) => {
          setEditValue(tagArray.filter(t => t !== tagValue));
        };

        // Use CountrySearchCombobox for searchable multi-select
        // Show 'Popular Countries' group only for country fields (by label or key)
        let popularOptions: OptionType[] = [];
        if (
          label?.toLowerCase().includes('country') ||
          label?.toLowerCase().includes('location') ||
          label?.toLowerCase().includes('data storage') ||
          label?.toLowerCase().includes('data location') ||
          label?.toLowerCase().includes('region')
        ) {
          const popular = getPopularCountries();
          popularOptions = normalizedOptions.filter(opt => popular.includes(opt.key));
        }
        inputNode = (
          <div className={"w-full"}>
            {/* Selected tags as badges */}
            <div className="flex flex-wrap gap-1 mb-2">
              {tagArray.map((tag, index) => (
                <Badge key={index} variant="editable" className="text-xs flex items-center">
                  {normalizedOptions.find(opt => opt.value === tag)?.key || tag}
                  <button
                    className="ml-1 text-xs hover:text-red-500"
                    onClick={() => handleRemoveTag(tag)}
                    type="button"
                  >
                    ×
                  </button>
                </Badge>
              ))}
            </div>
            {/* Searchable combobox for selecting tags */}
            <CountrySearchCombobox
              options={normalizedOptions}
              selected={tagArray}
              setSelected={setEditValue}
              placeholder={label ? `Select ${label}...` : 'Select...'}
              disabled={disabled}
              popularOptions={popularOptions}
            />
          </div>
        );
      } else {
        // No options: fallback to freeform tag input (original behavior)
        const tagArray = Array.isArray(editValue)
          ? editValue
          : (typeof editValue === 'string' && editValue)
            ? editValue.split(",").map(tag => tag.trim())
            : [];
        const handleAddTag = () => {
          if (newTag.trim() !== '') {
            setEditValue([...tagArray, newTag.trim()]);
            setNewTag('');
          }
        };

        const handleRemoveTag = (index: number) => {
          const newTags = [...tagArray];
          newTags.splice(index, 1);
          setEditValue(newTags);
        };

        const handleKeyDown = (e: React.KeyboardEvent) => {
          if (e.key === 'Enter') {
            e.preventDefault();
            handleAddTag();
          }
        };

        inputNode = (
          <div className={"w-full"}>
            <div className="flex flex-wrap gap-1 mb-2">
              {tagArray.map((tag, index) => (
                <Badge key={index} variant="editable" className="text-xs flex items-center">
                  {tag}
                  <button
                    className="ml-1 text-xs hover:text-red-500"
                    onClick={() => handleRemoveTag(index)}
                    type="button"
                  >
                    ×
                  </button>
                </Badge>
              ))}
            </div>
            <div className="flex">
              <Input
                type="text"
                value={newTag}
                onChange={e => setNewTag(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="Add new tag and press Enter"
                disabled={disabled}
                className={styles['editable-input']}
                style={{
                  backgroundColor: theme.isDark ? '#232326' : '#fff',
                  color: theme.isDark ? '#f3f4f6' : '#18181b',
                  border: `1px solid ${theme.isDark ? '#35353a' : '#e5e7eb'}`
                }}
              />
              <Button
                type="button"
                size="sm"
                onClick={handleAddTag}
                className="ml-2"
                style={{
                  background: colorValues[theme.primaryColor]?.primary ?? colorValues.default.primary,
                  color: theme.isDark ? '#fff' : '#18181b',
                  border: `1px solid ${colorValues[theme.primaryColor]?.primary ?? colorValues.default.primary}`
                }}
                disabled={disabled}
              >
                Add
              </Button>
            </div>
          </div>
        );
      }

    } else if (type === 'url') {
      inputNode = (
        <div className="w-full">
          <div className="flex items-center gap-2">
            <Input
              type="url"
              value={editValue as string}
              onChange={e => {
                setEditValue(e.target.value);
                // Trigger security check when URL changes
                // Debounced security check
                if (onSecurityCheck && showSecurityScore) {
                  if (debounceTimer.current) {
                    clearTimeout(debounceTimer.current);
                  }
                  debounceTimer.current = setTimeout(() => {
                    try {
                      const url = new URL(e.target.value);
                      if (
                        // (url.protocol === 'http:' || url.protocol === 'https:') &&
                        url.hostname.includes('.')
                      ) {
                        console.log('Triggering security check for URL:', e.target.value);
                        onSecurityCheck(undefined, e.target.value);
                      }
                    } catch {
                      // Invalid URL, do not trigger security check
                    }
                  }, 1000);
                }
              }}
              disabled={disabled}
              className={`${styles['editable-input']} flex-1`}
              style={{
                backgroundColor: theme.isDark ? '#232326' : '#fff',
                color: theme.isDark ? '#f3f4f6' : '#18181b',
                border: `1px solid ${theme.isDark ? '#35353a' : '#e5e7eb'}`
              }}
            />
            {showSecurityScore && (
              <div className="flex items-center">
                {isCheckingSecurity ? (
                  <div className="w-5 h-5 border-2 border-gray-400 border-t-transparent rounded-full animate-spin" />
                ) : securityCheckError?.includes('No score') ||
                   securityCheckError?.includes('No scores') ||
                   securityCheckError?.includes('No score available') ? (
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <span className="px-2 py-1 rounded text-xs font-medium text-gray-500 bg-gray-100 dark:bg-gray-800 dark:text-gray-400 cursor-help" onClick={() => onSecurityCheck(undefined, editValue as string)}>
                          No Score
                        </span>
                      </TooltipTrigger>
                      <TooltipContent className="max-w-[16rem] text-sm">
                        Upguard has no security score for this vendor. You can manually trigger a check by clicking the security score badge. We will automatically check again in 7 days to see if a score has been calculated.
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                ) : securityScore !== null && securityScore !== undefined ? (
                  <div
                    className={`px-2 py-1 rounded text-xs font-medium ${securityScore >= 750
                      ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                      : securityScore >= 500
                        ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200'
                        : 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'
                      }`}
                    title={`Security score: ${securityScore}/1000`}
                    onClick={() => onSecurityCheck(undefined, editValue as string)}
                  >
                    {securityScore}
                  </div>
                ) : securityCheckError ? (
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <span className="px-2 py-1 rounded text-xs font-medium text-red-500 bg-red-100 dark:bg-red-900 dark:text-red-200 cursor-help" onClick={() => onSecurityCheck(undefined, editValue as string)}>
                          Error
                        </span>
                      </TooltipTrigger>
                      <TooltipContent className="max-w-[16rem] text-sm">
                        {securityCheckError}
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                ) : null}
              </div>
            )}
          </div>
          {showSecurityScore && !isCheckingSecurity && !securityCheckError && securityScore === null && (
            <div className="text-xs text-gray-500 mt-1">
              Security score will appear after saving the URL
            </div>
          )}
        </div>
      );
    } else if (type === 'markdown') {
      // Auto-growing textarea for markdown that blends with the page
      // Auto-resize function
      const autoResize = () => {
        const textarea = markdownTextareaRef.current;
        if (textarea) {
          textarea.style.height = 'auto';
          textarea.style.height = textarea.scrollHeight + 'px';
        }
      };

      inputNode = (
        <textarea
          ref={markdownTextareaRef}
          value={editValue as string}
          onChange={e => {
            setEditValue(e.target.value);
            autoResize();
          }}
          disabled={disabled}
          placeholder="Write your content in Markdown...\n\nYou can use:\n• **bold** and *italic*\n• # Headings\n• - Lists\n• [Links](url)\n• And custom components like <CompanyName />"
          className="w-full resize-none overflow-hidden"
          style={{
            backgroundColor: 'transparent',
            color: theme.isDark ? '#e5e7eb' : '#1f2937',
            border: 'none',
            outline: 'none',
            padding: '0',
            fontFamily: 'inherit',
            fontSize: 'inherit',
            lineHeight: '1.75',
            minHeight: '100px'
          }}
        />
      );
    } else if (type === 'textarea') {
      inputNode = (
        <textarea
          value={editValue as string}
          onChange={e => setEditValue(e.target.value)}
          disabled={disabled}
          className={styles['editable-input']}
          rows={4}
          style={{
            backgroundColor: theme.isDark ? '#232326' : '#fff',
            color: theme.isDark ? '#f3f4f6' : '#18181b',
            border: `1px solid ${theme.isDark ? '#35353a' : '#e5e7eb'}`,
            width: '100%',
            padding: '8px',
            borderRadius: '4px',
            resize: 'vertical'
          }}
        />
      );
    } else if (type === 'singleuser') {
      const selectedUserId = typeof editValue === 'string'
        ? editValue
        : Array.isArray(editValue)
          ? editValue[0] ?? ''
          : '';

      inputNode = (
        <SingleUserCombobox
          selected={selectedUserId ?? ''}
          setSelected={(userId) => setEditValue(userId)}
          placeholder={`Select ${label?.toLowerCase() || 'user'}...`}
          className="w-full"
        />
      );
    } else if (type === 'multiuser') {
      const selectedUserIds = Array.isArray(editValue)
        ? editValue
        : (typeof editValue === 'string' && editValue)
          ? editValue.split(',').map(id => id.trim()).filter(Boolean)
          : [];

      inputNode = (
        <div className={fullWidth ? 'w-full' : 'max-w-md'}>
          <MultiUserCombobox
            selected={selectedUserIds}
            setSelected={(ids) => setEditValue(ids)}
            placeholder={`Select ${label?.toLowerCase() || 'users'}...`}
            className="w-full"
          />
        </div>
      );
    } else {
      inputNode = (
        <Input
          type="text"
          value={editValue as string}
          onChange={e => setEditValue(e.target.value)}
          disabled={disabled}
          className={styles['editable-input']}
          style={{
            backgroundColor: theme.isDark ? '#232326' : '#fff',
            color: theme.isDark ? '#f3f4f6' : '#18181b',
            border: `1px solid ${theme.isDark ? '#35353a' : '#e5e7eb'}`
          }}
        />
      );
    }
    return (
      <div className="editable-field property-item" ref={containerRef}>
        <Label className="property-label">{label}</Label>
        {inputNode}
        <div className="flex flex-row justify-end gap-2 mt-2 w-full">
          {!isCreateMode && (
            <>
              <Button
                size="sm"
                onClick={handleSave}
                disabled={disabled}
                className={`${styles['action-button']} editable-field-save-button`}
                style={{
                  background: colorValues[theme.primaryColor]?.primary ?? colorValues.default.primary,
                  color: theme.isDark ? '#fff' : '#18181b',
                  border: `1px solid ${colorValues[theme.primaryColor]?.primary ?? colorValues.default.primary}`
                }}
              >
                Save
              </Button>
              <Button size="sm" variant="outline" onClick={handleCancel} className={styles['action-button']}>Cancel</Button>
            </>
          )}
        </div>
      </div>
    );
  }


  // Check if field should be hidden based on the hidden condition and formValues
  // Secondary hide logic: hide if shouldHide condition is met and this field is in the list
  // console.log('shouldHide', shouldHide);
  // console.log('id', id);
  if (shouldHide && shouldHide.when && shouldHide.fields.includes(id)) {
    return null;
  }

  if (hidden && formValues[hidden.key] === hidden.value) {
    return null; // Don't render anything if the field should be hidden
  }

  // Display mode
  return (
    <div className="editable-field property-item" ref={containerRef}>
      <Label className="property-label">{label}</Label>
      <div
        className={[
          styles['property-value'],
          editing ? styles['editing'] : '',
          readOnly ? 'read-only' : '',
        ].join(' ')}
        onClick={e => {
          // Completely prevent click handling when read-only
          if (disabled || readOnly) {
            e.stopPropagation();
            return;
          }
          // If using controlled editing, only activate this field if not already editing
          if (controlledSetEditing) {
            if (!editing) controlledSetEditing(true);
          } else {
            setLocalEditing(true);
          }
          e.stopPropagation();
        }}
        style={{
          cursor: disabled || readOnly ? 'default' : 'pointer',
          backgroundColor: theme.isDark ? 'var(--bg-secondary)' : '#fff',
          color: theme.isDark ? '#f3f4f6' : '#18181b',
          border: 'none',
          pointerEvents: readOnly ? 'none' : 'auto' // Disable pointer events completely when read-only
        }}
      >
        {type === 'controlcombobox' && Array.isArray(value) ? (
          <div className="flex flex-wrap gap-1">
            {value.length > 0 ? value.map((control, index) => (
              <Badge key={index} variant="secondary" className="text-xs">
                {control}
              </Badge>
            )) : '-'}
          </div>
        ) : type === 'datatypecombobox' && Array.isArray(value) ? (
          <DataTypesProvider>
            <RenderDataTypeBadges ids={value} />
          </DataTypesProvider>
        ) : type === 'legalobligations' && Array.isArray(value) ? (
          <LegalObligationsProvider>
            <RenderLegalObligationBadges ids={value} />
          </LegalObligationsProvider>
        ) : type === 'tags' && Array.isArray(value) ? (
          <div className="flex flex-wrap gap-1">
            {value.length > 0 ? value.map((tag, index) => (
              <Badge key={index} variant="editable" className="text-xs">
                {tag}
              </Badge>
            )) : '-'}
          </div>
        ) : type === 'date' ? (
          renderDate(value)
        ) : type === 'url' ? (
          <div className="flex items-center gap-2">
            {value ? (
              <a
                href={value as string}
                target="_blank"
                rel="noopener noreferrer"
                className="text-primary hover:underline"
                onClick={(e) => e.stopPropagation()} // Prevent click from bubbling up to parent
              >
                {value || '-'}
              </a>
            ) : (
              '-'
              )}
            {showSecurityScore && securityScore !== null && securityScore !== undefined && (
              <span
                className={`px-2 py-0.5 rounded text-xs font-medium ${securityScore >= 90
                  ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                  : securityScore >= 70
                    ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200'
                    : 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'
                  }`}
                title={`Security score: ${securityScore}/100`}
              >
                {securityScore}
              </span>
            )}
          </div>
        ) : type === 'select' && options?.length > 0 && typeof options[0] === 'object' && 'key' in options[0] ? (
          (options as OptionType[]).find(opt => opt.value === value)?.key || value
        ) : type === 'riskscombobox' && Array.isArray(value) ? (
          <div className="flex flex-wrap gap-1">
            {value.length > 0 ? value.map((riskId, index) => (
              <Badge key={index} variant="secondary" className="text-xs">
                {riskId}
              </Badge>
            )) : '-'}
          </div>
        ) : type === 'assetscombobox' && Array.isArray(value) ? (
          <div className="flex flex-wrap gap-1">
            {value.length > 0 ? value.map((assetId, index) => (
              <Badge key={index} variant="secondary" className="text-xs">
                {assetId}
              </Badge>
            )) : '-'}
          </div>
        ) : type === 'singleuser' ? (
          userIdToName?.[value as string] || (value as string) || '-'
        ) : type === 'multiuser' ? (
          (() => {
            const ids = Array.isArray(value)
              ? value
              : (typeof value === 'string' && value)
                ? value.split(',').map(id => id.trim()).filter(Boolean)
                : [];
            if (!ids.length) {
              return '-';
            }
            return (
              <div className="flex flex-wrap gap-1">
                {ids.map((id, index) => (
                  <Badge key={`${id}-${index}`} variant="secondary" className="text-xs">
                    {userIdToName?.[id] || id}
                  </Badge>
                ))}
              </div>
            );
          })()
        ) : (
          value
        )}
        {/* Never show edit icon when disabled or readOnly is true */}
        {!disabled && !readOnly && (
          <span className={`${styles['edit-icon']} ${pencilAlways ? styles['pencil-always'] : ''}`}>
            <FontAwesomeIcon icon={faPenToSquare} className="w-4 h-4" />
          </span>
        )}
      </div>
    </div>
  );
};

export default EditableField;
export { EditableField };
